/**
 * Stripe Checkout deep-link bridge for native (iOS/Android).
 *
 * Pattern (used by Uber, Cash App, Revolut, and Stripe's own native
 * sample apps):
 *
 *   1. Stripe Checkout's success_url / cancel_url point at a public
 *      bridge page on our website (`/payment-complete`).
 *   2. That bridge page immediately redirects to a custom URL scheme:
 *      `elefa://payment-complete?status=success&session_id=…`.
 *   3. iOS/Android recognise the scheme, dismiss the in-app browser
 *      sheet (SFSafariViewController / Chrome Custom Tab) automatically,
 *      and reopen the host app.
 *   4. This listener catches the App `appUrlOpen` event, closes the
 *      Capacitor Browser sheet defensively, and navigates to the real
 *      in-app success / cancel screen - never to a website page.
 *
 * Without this bridge, Stripe's "back to merchant" arrow loads the
 * cancel_url *inside* the in-app browser, leaving the user trapped on
 * the elefa.tel website and forcing them to tap the manual close
 * button. This hook removes that trap entirely.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App, type URLOpenListenerEvent } from "@capacitor/app";

export const CHECKOUT_DEEPLINK_HOST = "payment-complete";
export const CHECKOUT_DEEPLINK_SCHEME = "elefa";
const CHECKOUT_WEB_HOSTS = new Set(["elefa.tel", "www.elefa.tel"]);

/**
 * Build the deep-link URL the bridge page will redirect to. Centralised
 * so the website page and the native listener agree on the contract.
 */
export function buildCheckoutDeepLink(params: URLSearchParams): string {
  const qs = params.toString();
  return `${CHECKOUT_DEEPLINK_SCHEME}://${CHECKOUT_DEEPLINK_HOST}${qs ? `?${qs}` : ""}`;
}

export default function useCheckoutDeepLink() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let handle: Awaited<ReturnType<typeof App.addListener>> | null = null;

    const routeFromUrl = async (rawUrl?: string | null) => {
      if (!rawUrl) return;
      try {
        const url = new URL(rawUrl);
        const isCustomScheme =
          url.protocol.replace(":", "") === CHECKOUT_DEEPLINK_SCHEME &&
          url.hostname === CHECKOUT_DEEPLINK_HOST;
        const isUniversalLink =
          (url.protocol === "https:" || url.protocol === "http:") &&
          CHECKOUT_WEB_HOSTS.has(url.hostname) &&
          url.pathname === `/${CHECKOUT_DEEPLINK_HOST}`;
        if (!isCustomScheme && !isUniversalLink) return;

        // Close any in-app browser sheet that might still be on screen.
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.close();
        } catch {
          /* sheet already gone - ignore */
        }

        const status = url.searchParams.get("status");
        const sessionId = url.searchParams.get("session_id");
        const amount = url.searchParams.get("amount");

        if (status === "success" && sessionId) {
          const target = `/connect-portal/topup/success?session_id=${encodeURIComponent(
            sessionId,
          )}${amount ? `&amount=${encodeURIComponent(amount)}` : ""}`;
          navigateRef.current(target, { replace: true });
        } else {
          // Cancel / unknown → return user to the wallet, no scary screens.
          navigateRef.current("/connect-portal/topup", { replace: true });
        }
      } catch {
        /* malformed URL - let the OS handle it */
      }
    };

    const handler = (event: URLOpenListenerEvent) => routeFromUrl(event.url);

    App.addListener("appUrlOpen", handler).then((h) => {
      handle = h;
    });

    // If the app was cold-launched by the return link, the listener above may
    // attach after the OS delivered the URL. Always consume the launch URL too.
    // getLaunchUrl() resolves to `null` on a normal launch (no deep link) —
    // destructuring without a guard throws "Cannot destructure 'url' from null"
    // on every cold launch.
    App.getLaunchUrl()
      .then((res) => {
        const url = res?.url;
        if (url) routeFromUrl(url);
      })
      .catch(() => {});

    return () => {
      handle?.remove();
    };
  }, []);
}
